import { Handle, Position, useReactFlow } from '@xyflow/react'
import { Layers, Play, Settings, FileCode, Link, Download } from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import styles from '@/styles/NodeGroupBlueprint.module.css'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'

interface BlueprintMapping {
  input: string
  parameter: string
  type: 'string' | 'number' | 'boolean' | 'object'
}

interface NodeGroupBlueprintData {
  label: string
  informationClassification: string
  createdBy: string
  createdAt: string
  blueprintReference: string
  inputMappings: string
  outputMappings: string
  inheritSettings: boolean
  blueprintId: string | null
  configuration: Record<string, any>
  statusMessage: string
  isMinimized: boolean
  isLocked: boolean
  isExecuting?: boolean
  lastExecuted?: string
  executionResult?: any
  inputParameterMappings?: BlueprintMapping[]
  outputParameterMappings?: BlueprintMapping[]
}

interface NodeGroupBlueprintProps {
  id: string
  data: NodeGroupBlueprintData
  selected?: boolean
}

export default function NodeGroupBlueprint({ id, data, selected }: NodeGroupBlueprintProps) {  const { updateNodeData } = useReactFlow()
  const { toast } = useToast()
  
  // ✅ Initialize with safe defaults (not from collaborative state)
  const [blueprintReference, setBlueprintReference] = useState('')
  const [inputMappings, setInputMappings] = useState('')
  const [outputMappings, setOutputMappings] = useState('')
  const [inheritSettings, setInheritSettings] = useState(false)
  const [blueprintId, setBlueprintId] = useState<string | null>(null)
  const [configuration, setConfiguration] = useState<Record<string, any>>({})
  const [isExecuting, setIsExecuting] = useState(false)
  const [executionResult, setExecutionResult] = useState<any>(null)
  const [inputParameterMappings, setInputParameterMappings] = useState<BlueprintMapping[]>([])
  const [outputParameterMappings, setOutputParameterMappings] = useState<BlueprintMapping[]>([])

  // ✅ Sync from collaborative state (separate useEffects)
  useEffect(() => {
    setBlueprintReference(data.blueprintReference || '')
  }, [data.blueprintReference])

  useEffect(() => {
    setInputMappings(data.inputMappings || '')
  }, [data.inputMappings])

  useEffect(() => {
    setOutputMappings(data.outputMappings || '')
  }, [data.outputMappings])

  useEffect(() => {
    setInheritSettings(data.inheritSettings || false)
  }, [data.inheritSettings])

  useEffect(() => {
    setBlueprintId(data.blueprintId || null)
  }, [data.blueprintId])

  useEffect(() => {
    setConfiguration(data.configuration || {})
  }, [data.configuration])

  useEffect(() => {
    setIsExecuting(data.isExecuting || false)
  }, [data.isExecuting])

  useEffect(() => {
    setExecutionResult(data.executionResult || null)
  }, [data.executionResult])

  useEffect(() => {
    setInputParameterMappings(data.inputParameterMappings || [])
  }, [data.inputParameterMappings])

  useEffect(() => {
    setOutputParameterMappings(data.outputParameterMappings || [])
  }, [data.outputParameterMappings])

  // ✅ Collaborative sync function (only call on user actions)
  const syncNodeData = useCallback((updates: Partial<NodeGroupBlueprintData>) => {
    updateNodeData(id, { ...data, ...updates })
  }, [id, data, updateNodeData])

  // Load blueprint
  const loadBlueprint = useCallback(async () => {
    if (!blueprintReference) {
      toast({
        title: 'No Blueprint Reference',
        description: 'Please enter a blueprint reference first',
        variant: 'destructive'
      })
      return
    }

    try {
      // TODO: Implement actual blueprint loading from API      setBlueprintId(blueprintReference)
      
      // Mock blueprint loading - replace with actual API call
      const mockInputParams: BlueprintMapping[] = [
        { input: 'userInput', parameter: 'sourceText', type: 'string' },
        { input: 'options', parameter: 'processingOptions', type: 'object' }
      ]
      
      const mockOutputParams: BlueprintMapping[] = [
        { input: 'result', parameter: 'processedOutput', type: 'string' },
        { input: 'metadata', parameter: 'executionMetadata', type: 'object' }
      ]
      
      setInputParameterMappings(mockInputParams)
      setOutputParameterMappings(mockOutputParams)
      
      // Sync to collaborative state
      syncNodeData({
        blueprintId: blueprintReference,
        inputParameterMappings: mockInputParams,
        outputParameterMappings: mockOutputParams
      })
      
      toast({
        title: 'Blueprint Loaded',
        description: `Loaded blueprint: ${blueprintReference}`
      })
    } catch (error) {
      toast({
        title: 'Error Loading Blueprint',
        description: 'Failed to load the specified blueprint',
        variant: 'destructive'
      })
    }
  }, [blueprintReference, syncNodeData, toast])

  // Execute blueprint
  const executeBlueprint = useCallback(async () => {
    if (!blueprintId) {
      toast({
        title: 'No Blueprint Loaded',
        description: 'Please load a blueprint first',
        variant: 'destructive'
      })
      return
    }

    setIsExecuting(true)
    try {
      // TODO: Implement actual blueprint execution
      
      // Mock execution - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const mockResult = {
        success: true,
        output: 'Blueprint executed successfully',
        timestamp: new Date().toISOString(),
        executionTime: '2.1s'
      }
        setExecutionResult(mockResult)
      syncNodeData({ 
        lastExecuted: new Date().toISOString(),
        executionResult: mockResult,
        isExecuting: false
      })
      
      toast({
        title: 'Blueprint Executed',
        description: 'Blueprint execution completed successfully'
      })
    } catch (error) {
      toast({
        title: 'Execution Failed',
        description: 'Blueprint execution failed',
        variant: 'destructive'
      })
    } finally {
      setIsExecuting(false)
    }
  }, [blueprintId, toast, syncNodeData])
  // Add parameter mapping
  const addInputMapping = useCallback(() => {
    const newMapping: BlueprintMapping = {
      input: '',
      parameter: '',
      type: 'string'
    }
    const newMappings = [...inputParameterMappings, newMapping]
    setInputParameterMappings(newMappings)
    syncNodeData({ inputParameterMappings: newMappings })
  }, [inputParameterMappings, syncNodeData])
  const addOutputMapping = useCallback(() => {
    const newMapping: BlueprintMapping = {
      input: '',
      parameter: '',
      type: 'string'
    }
    const newMappings = [...outputParameterMappings, newMapping]
    setOutputParameterMappings(newMappings)
    syncNodeData({ outputParameterMappings: newMappings })
  }, [outputParameterMappings, syncNodeData])
  // Update parameter mapping
  const updateInputMapping = useCallback((index: number, updates: Partial<BlueprintMapping>) => {
    const updated = [...inputParameterMappings]
    updated[index] = { ...updated[index], ...updates }
    setInputParameterMappings(updated)
    syncNodeData({ inputParameterMappings: updated })
  }, [inputParameterMappings, syncNodeData])
  const updateOutputMapping = useCallback((index: number, updates: Partial<BlueprintMapping>) => {
    const updated = [...outputParameterMappings]
    updated[index] = { ...updated[index], ...updates }
    setOutputParameterMappings(updated)
    syncNodeData({ outputParameterMappings: updated })
  }, [outputParameterMappings, syncNodeData])

  // Export configuration
  const exportConfiguration = useCallback(() => {
    const config = {
      blueprintReference,
      inputMappings,
      outputMappings,
      inheritSettings,
      inputParameterMappings,
      outputParameterMappings,
      configuration
    }
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `blueprint-config-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
    
    toast({
      title: 'Configuration Exported',
      description: 'Blueprint configuration has been exported'
    })
  }, [blueprintReference, inputMappings, outputMappings, inheritSettings, inputParameterMappings, outputParameterMappings, configuration, toast])
  return (
    <NodeBaseContainer
      id={id}
      data={data}
      selected={selected}
      nodeTitle={data.label || 'Group Blueprint'}
      isLocked={data.isLocked}
      isMinimized={data.isMinimized}
      className={`${styles.nodeGroupBlueprint} min-w-[900px] min-h-[630px]`}      resizable={true}
      minWidth={400}
      minHeight={300}
    >
      <Handle type="target" position={Position.Left} />
      
      <div className={styles.nodeContent}>
        <div className={styles.nodeMainContent}>
          <div className={styles.contentStack}>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Layers className="h-5 w-5 text-pink-600" />
                <span className="font-semibold">Group Blueprint</span>
                {blueprintId && <Badge variant="outline">Loaded</Badge>}
                {isExecuting && <Badge variant="secondary">Executing...</Badge>}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadBlueprint}
                  disabled={!blueprintReference || isExecuting}
            >
              <Link className="h-4 w-4 mr-1" />
              Load
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={executeBlueprint}
              disabled={!blueprintId || isExecuting}
            >
              <Play className="h-4 w-4 mr-1" />
              Execute
            </Button>
            <Button variant="outline" size="sm" onClick={exportConfiguration}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-2 gap-4">
          {/* Configuration Panel */}
          <div className="space-y-4">
            <Card className="h-fit">
              <CardHeader className="pb-3" hasCardAction={false}>
                <CardTitle className="text-sm">Blueprint Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="blueprintReference">Blueprint Reference</Label>                  <Input
                    id="blueprintReference"
                    value={blueprintReference}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setBlueprintReference(newValue)
                      syncNodeData({ blueprintReference: newValue })
                    }}
                    placeholder="blueprint-id or blueprint-name"
                  />
                </div>
                  <div className="flex items-center space-x-2">                  <Checkbox
                    id="inheritSettings"
                    checked={inheritSettings}
                    onCheckedChange={(checked) => {
                      const newValue = checked as boolean
                      setInheritSettings(newValue)
                      syncNodeData({ inheritSettings: newValue })
                    }}
                  />
                  <Label htmlFor="inheritSettings">Inherit parent blueprint settings</Label>
                </div>
                
                <div>
                  <Label htmlFor="inputMappings">Input Mappings (JSON)</Label>                  <Textarea
                    id="inputMappings"
                    value={inputMappings}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setInputMappings(newValue)
                      syncNodeData({ inputMappings: newValue })
                    }}
                    placeholder="Enter input mappings as JSON"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="outputMappings">Output Mappings (JSON)</Label>                  <Textarea
                    id="outputMappings"
                    value={outputMappings}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setOutputMappings(newValue)
                      syncNodeData({ outputMappings: newValue })
                    }}
                    placeholder="Enter output mappings as JSON"
                    rows={3}
                  />
                </div>
                  <div>
                  <Label htmlFor="configuration">Configuration (JSON)</Label>
                  <Textarea
                    id="configuration"
                    value={typeof configuration === 'string' ? configuration : JSON.stringify(configuration, null, 2)}                    onChange={(e) => {
                      try {
                        const newConfig = JSON.parse(e.target.value)
                        setConfiguration(newConfig)
                        syncNodeData({ configuration: newConfig })
                      } catch {
                        // For invalid JSON, store as empty object for now
                        const emptyConfig = {}
                        setConfiguration(emptyConfig)
                        syncNodeData({ configuration: emptyConfig })
                      }
                    }}
                    placeholder="Enter configuration as JSON"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Input Mappings */}
            <Card className="h-fit">
              <CardHeader className="pb-3" hasCardAction={false}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Input Mappings ({inputParameterMappings.length})</CardTitle>
                  <Button variant="outline" size="sm" onClick={addInputMapping}>
                    <Settings className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[200px] overflow-y-auto">
                {inputParameterMappings.map((mapping, index) => (
                  <div key={index} className="border rounded p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Input Field</Label>
                        <Input
                          value={mapping.input}
                          onChange={(e) => updateInputMapping(index, { input: e.target.value })}
                          placeholder="input_field"
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Blueprint Parameter</Label>
                        <Input
                          value={mapping.parameter}
                          onChange={(e) => updateInputMapping(index, { parameter: e.target.value })}
                          placeholder="blueprint_param"
                          className="h-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={mapping.type}
                        onValueChange={(value: any) => updateInputMapping(index, { type: value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="object">Object</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {inputParameterMappings.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No input mappings configured
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Output & Execution Panel */}
          <div className="space-y-4">
            {/* Output Mappings */}
            <Card className="h-fit">
              <CardHeader className="pb-3" hasCardAction={false}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Output Mappings ({outputParameterMappings.length})</CardTitle>
                  <Button variant="outline" size="sm" onClick={addOutputMapping}>
                    <Settings className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[200px] overflow-y-auto">
                {outputParameterMappings.map((mapping, index) => (
                  <div key={index} className="border rounded p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Output Field</Label>
                        <Input
                          value={mapping.input}
                          onChange={(e) => updateOutputMapping(index, { input: e.target.value })}
                          placeholder="output_field"
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Blueprint Output</Label>
                        <Input
                          value={mapping.parameter}
                          onChange={(e) => updateOutputMapping(index, { parameter: e.target.value })}
                          placeholder="blueprint_output"
                          className="h-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select
                        value={mapping.type}
                        onValueChange={(value: any) => updateOutputMapping(index, { type: value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="object">Object</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {outputParameterMappings.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No output mappings configured
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Execution Results */}
            <Card className="h-fit">
              <CardHeader className="pb-3" hasCardAction={false}>
                <CardTitle className="text-sm">Execution Results</CardTitle>
              </CardHeader>
              <CardContent>
                {executionResult ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant={executionResult.success ? "default" : "destructive"}>
                        {executionResult.success ? 'Success' : 'Failed'}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {executionResult.executionTime}
                      </span>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <Label className="text-xs">Output</Label>
                      <div className="bg-gray-50 p-3 rounded text-sm mt-1">
                        <pre className="whitespace-pre-wrap">
                          {JSON.stringify(executionResult.output, null, 2)}
                        </pre>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      Executed: {new Date(executionResult.timestamp).toLocaleString()}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileCode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No execution results yet</p>
                  </div>
                )}              </CardContent>
            </Card>
          </div>
        </div>
          </div>
        </div>
      </div>
      
      <Handle type="source" position={Position.Right} />
    </NodeBaseContainer>
  )
}
